import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  addressAutocompleteService,
  AutocompleteSuggestion,
} from '@/services/addressAutocompleteService';
import type { AddressInput } from './AddressValidationInput';

interface AddressAutocompleteInputProps {
  value: AddressInput;
  onChange: (address: AddressInput) => void;
  onValidationChange?: (result: any) => void;
  required?: boolean;
  label?: string;
  disabled?: boolean;
}

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  required = false,
  label = 'Address',
  disabled = false,
}) => {
  const [options, setOptions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  // Local state for all fields to avoid re-rendering parent on every keystroke
  const [streetInput, setStreetInput] = useState(value.street || '');
  const [cityInput, setCityInput] = useState(value.city || '');
  const [stateInput, setStateInput] = useState(value.state || '');
  const [zipInput, setZipInput] = useState(value.zip || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Keep a ref to the latest local values so blur handlers always see current state
  const localRef = useRef({ street: streetInput, city: cityInput, state: stateInput, zip: zipInput });
  localRef.current = { street: streetInput, city: cityInput, state: stateInput, zip: zipInput };

  // Sync local state when parent value changes externally (e.g. clear)
  useEffect(() => {
    setStreetInput(value.street || '');
    setCityInput(value.city || '');
    setStateInput(value.state || '');
    setZipInput(value.zip || '');
  }, [value.street, value.city, value.state, value.zip]);

  const hasAnyData = !!(streetInput.trim() || cityInput.trim() || stateInput.trim() || zipInput.trim());

  const flushToParent = useCallback((overrides?: Partial<AddressInput>) => {
    const merged = { ...localRef.current, ...overrides, country: value.country || 'US' };
    onChange(merged);
  }, [onChange, value.country]);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (input.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const suggestions = await addressAutocompleteService.getSuggestions(input);
      setOptions(suggestions);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStreetInputChange = (_: any, newValue: string, reason: string) => {
    setStreetInput(newValue);

    if (reason === 'input') {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, 300);
    }
  };

  const handleSelect = async (_: any, suggestion: AutocompleteSuggestion | string | null) => {
    if (!suggestion || typeof suggestion === 'string') {
      return;
    }

    setLoading(true);
    try {
      const details = await addressAutocompleteService.getPlaceDetails(suggestion.placeId);
      if (details) {
        setStreetInput(details.street);
        setCityInput(details.city);
        setStateInput(details.state);
        setZipInput(details.zip);
        onChange({
          street: details.street,
          city: details.city,
          state: details.state,
          zip: details.zip,
          country: details.country,
        });
      }
    } catch {
      setStreetInput(suggestion.mainText);
      flushToParent({ street: suggestion.mainText });
    } finally {
      setLoading(false);
      setOptions([]);
      addressAutocompleteService.newSession();
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
          {label} {(required || hasAnyData) && '*'}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            options={options}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.mainText
            }
            filterOptions={(x) => x}
            inputValue={streetInput}
            onInputChange={handleStreetInputChange}
            onChange={handleSelect}
            loading={loading}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Street Address"
                fullWidth
                required={hasAnyData}
                onBlur={() => flushToParent()}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.placeId}>
                <Box>
                  <Typography variant="body2">{option.mainText}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.secondaryText}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText={streetInput.length < 3 ? 'Type at least 3 characters' : 'No addresses found'}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="City"
            fullWidth
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            onBlur={() => flushToParent()}
            disabled={disabled}
            required={hasAnyData}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="State"
            fullWidth
            value={stateInput}
            onChange={(e) => setStateInput(e.target.value)}
            onBlur={() => flushToParent()}
            disabled={disabled}
            required={hasAnyData}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="ZIP Code"
            fullWidth
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value)}
            onBlur={() => flushToParent()}
            disabled={disabled}
            required={hasAnyData}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddressAutocompleteInput;
